export enum NotificationType {
	/**
	 * Generic welcome email — the template ships this as the one reference
	 * notification type. Add more entries as the app grows.
	 */
	WELCOME_EMAIL = 'welcome_email',
	/** The first-login invitation for a provisioned user (TO-309). */
	INVITATION = 'invitation',
	/** A content localization awaits review — sent to approvers (TO-430). */
	CONTENT_REVIEW_REQUESTED = 'content_review_requested',
	/** A content localization was approved — sent to its author (TO-430). */
	CONTENT_APPROVED = 'content_approved',
	/** A content localization was rejected — sent to its author (TO-430). */
	CONTENT_REJECTED = 'content_rejected',
	/** A content localization went live — sent to the tenant's product owners (TO-430). */
	CONTENT_PUBLISHED = 'content_published',
	/** A published content localization is about to expire (TO-430). */
	CONTENT_EXPIRING = 'content_expiring',
	/** An outbound webhook subscription disabled itself after repeated failures — sent to org admins (TO-381). */
	WEBHOOK_SUBSCRIPTION_DISABLED = 'webhook_subscription_disabled',
	/** An admin's test SMS from the console's SMS section (TO-383). Never subject to the type allowlist. */
	SMS_TEST = 'sms_test',
	/** The tenant's monthly SMS spend cap blocked a send — sent to its org admins (TO-383). */
	SMS_SPEND_CAP_REACHED = 'sms_spend_cap_reached',
	/** An offer was sent — the customer gets its link on each chosen channel (TO-465). */
	OFFER_SENT = 'offer_sent',
	/** A one-time code that unlocks an OTP-gated public landing (TO-478). Transactional — see {@link TRANSACTIONAL_ACCESS_TYPES}. */
	LANDING_ACCESS_OTP = 'landing_access_otp',
	/** A single-use link that unlocks a MAGIC_LINK-gated public landing (TO-478). Transactional — see {@link TRANSACTIONAL_ACCESS_TYPES}. */
	LANDING_ACCESS_MAGIC_LINK = 'landing_access_magic_link',
	/** A scheduled import file was not received by its deadline — sent to tenant admins (TO-398). */
	FILE_EXCHANGE_FILE_MISSING = 'file_exchange_file_missing',
	/** An import file was rejected by the malware scan — sent to tenant admins (TO-398). */
	FILE_EXCHANGE_SECURITY_REJECTED = 'file_exchange_security_rejected',
	/** An import file failed parsing, transformation or validation — sent to tenant admins (TO-398). */
	FILE_EXCHANGE_INVALID_FILE = 'file_exchange_invalid_file',
	/** An import completed with rejected records — sent to tenant admins (TO-398). */
	FILE_EXCHANGE_COMPLETED_WITH_ERRORS = 'file_exchange_completed_with_errors',
	/** A subject access bundle is ready — sent to the requesting compliance officer (TO-232). */
	SAR_READY = 'sar_ready',
}

/**
 * Transactional access-proof notifications (TO-478 transactional-access-notifications
 * REQ-101/102): sent without the MARKETING consent gate, the tenant SMS type
 * allowlist or the tenant toggles — but still bound by the shape check, country
 * allowlist, non-prod recipient allowlist, spend cap and provider opt-out.
 */
export const TRANSACTIONAL_ACCESS_TYPES: ReadonlySet<NotificationType> = new Set([
	NotificationType.LANDING_ACCESS_OTP,
	NotificationType.LANDING_ACCESS_MAGIC_LINK,
])

export enum NotificationChannel {
	EMAIL = 'email',
	SMS = 'sms',
}

export enum NotificationStatus {
	PENDING = 'pending',
	SENT = 'sent',
	/** The provider reported the message delivered to the handset (Twilio receipt, TO-474). */
	DELIVERED = 'delivered',
	FAILED = 'failed',
}

/**
 * Why a notification ended `failed` without a retry (TO-474). Stored in
 * `notifications.failure_code` and carried on the `*_failed` events.
 */
export enum NotificationFailureCode {
	/** The customer's `(MARKETING, <channel>)` consent is not `GRANTED`, or an SMS named no customer. */
	CONSENT_MISSING = 'CONSENT_MISSING',
	/** The destination is malformed or rejected by the provider as unreachable. */
	INVALID_RECIPIENT = 'INVALID_RECIPIENT',
	/** The request itself is malformed (e.g. an SMS body that is blank or too long). */
	INVALID_REQUEST = 'INVALID_REQUEST',
	/** The provider holds an opt-out for the recipient (Twilio 21610). */
	RECIPIENT_OPTED_OUT = 'RECIPIENT_OPTED_OUT',
	/** The provider refused the message for any other non-retryable reason. */
	PROVIDER_REJECTED = 'PROVIDER_REJECTED',
	/** The provider accepted the message but later reported it undelivered. */
	PROVIDER_UNDELIVERED = 'PROVIDER_UNDELIVERED',
	/** The tenant switched this notification type off for the channel (TO-382). */
	DISABLED_BY_TENANT = 'DISABLED_BY_TENANT',
	/** A consent-gated (marketing) email could not get its one-click unsubscribe link, so it was not sent (TO-138). */
	UNSUBSCRIBE_LINK_UNAVAILABLE = 'UNSUBSCRIBE_LINK_UNAVAILABLE',
	/** The tenant's SMS settings do not allow this notification type by SMS (TO-383). */
	SMS_TYPE_NOT_ALLOWED = 'SMS_TYPE_NOT_ALLOWED',
	/** The destination's country is not in the tenant's allowed SMS countries (TO-383). */
	SMS_COUNTRY_NOT_ALLOWED = 'SMS_COUNTRY_NOT_ALLOWED',
	/** The send would take the tenant's month SMS spend over its cap (TO-383). */
	SMS_SPEND_CAP_REACHED = 'SMS_SPEND_CAP_REACHED',
	/** A test SMS: the provider rejected the tenant's credentials (TO-383; test only, never retried). */
	PROVIDER_AUTH_FAILED = 'PROVIDER_AUTH_FAILED',
	/** A test SMS: the provider or the tenant's binding was unavailable (TO-383; test only, never retried). */
	PROVIDER_UNAVAILABLE = 'PROVIDER_UNAVAILABLE',
	/** customer-service reports the customer's email as suppressed (hard bounce, complaint, repeated soft bounces — TO-136). */
	RECIPIENT_SUPPRESSED = 'RECIPIENT_SUPPRESSED',
}

/** How SES classified a bounce (TO-136): `Permanent` → permanent; `Transient`/`Undetermined` → transient. */
export enum EmailBounceType {
	PERMANENT = 'permanent',
	TRANSIENT = 'transient',
}

/** Email delivery providers (ADR-0050). */
export enum EmailProviderCode {
	SES = 'ses',
}

/** SMS delivery providers (ADR-0050). */
export enum SmsProviderCode {
	TWILIO = 'twilio',
}

/**
 * Named cross-tenant (`withBypassRls`) read operations the notification context
 * performs, so the catalog of RLS-bypassing reads is discoverable from one enum
 * rather than scattered string literals (the value is the audited `operation`
 * label on the `TENANT_RLS_BYPASSED` event).
 */
export enum NotificationBypassOperation {
	/** Find every tenant that texted a phone on the shared sender, for STOP/START fan-out (TO-474 D4 = A). */
	LIST_SMS_TENANTS_BY_PHONE = 'notifications.listSmsTenantsByPhone',
	/** Daily purge of SES-feedback idempotency records older than 30 days, across tenants (TO-136). */
	PURGE_SES_FEEDBACK_EVENTS = 'notifications.purgeSesFeedbackEvents',
}

/** The SMS reply keywords that change a recipient's SMS marketing consent (TO-474). */
export enum SmsOptKeyword {
	STOP = 'STOP',
	STOPALL = 'STOPALL',
	UNSUBSCRIBE = 'UNSUBSCRIBE',
	CANCEL = 'CANCEL',
	END = 'END',
	QUIT = 'QUIT',
	START = 'START',
	UNSTOP = 'UNSTOP',
	YES = 'YES',
}

/**
 * Compile-time-checked names for MJML templates registered in
 * `email-templates.ts`. Producers (workers, services) MUST reference
 * an enum member when emitting `NOTIFICATION_EMAIL_REQUESTED` so a typo
 * in `templateName` becomes a build error rather than a runtime fallback.
 *
 * Each value matches the basename of an `.mjml` file under
 * `packages/core/utils/src/email/templates/`.
 */
export enum EmailTemplateName {
	WELCOME_EMAIL = 'welcome-email',
	INVITATION_EMAIL = 'invitation-email',
	CONTENT_NOTIFICATION = 'content-notification',
	WEBHOOK_SUBSCRIPTION_DISABLED = 'webhook-subscription-disabled',
	SMS_SPEND_CAP_REACHED = 'sms-spend-cap-reached',
	OFFER_SENT = 'offer-sent',
	/** The single-use link that unlocks a MAGIC_LINK landing (TO-478). */
	LANDING_ACCESS_MAGIC_LINK = 'landing-access-magic-link',
	/** The four file-exchange monitoring alerts (TO-398). */
	FILE_EXCHANGE_ALERT = 'file-exchange-alert',
	/** A subject access bundle is ready — the officer's notice (TO-232). */
	SAR_READY = 'sar-ready',
}

/**
 * Languages a notification email's copy is written in. A tenant's default
 * locale picks one by its primary language subtag; anything else is English.
 */
export enum EmailLanguage {
	EN = 'en',
	ES = 'es',
}
